// Who an expense claim at the FINANCE stage is waiting on.
//
// One rule, shared by the finance inbox, the decision and the notification:
// the finance stage is handled by people who hold expenses:finance for the
// expense's company (company access via Roles & permissions → Assignments;
// all-companies holders cover every company). This notice makes that rule
// visible on the claim — including when nobody satisfies it, which must never
// read as "all clear" (no-data-is-not-good-news).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock } from 'lucide-react';
import type { FinanceStage } from '@/lib/expensesApi';

export type FinanceStageState =
  | { kind: 'waiting'; company: string | null }
  | { kind: 'unknown'; company: string | null }
  | { kind: 'nobody'; company: string | null }
  | { kind: 'no_company' };

/** Pure: the four states of the finance stage. */
export function financeStageState(fs: FinanceStage | null | undefined): FinanceStageState | null {
  if (!fs) return null;
  if (fs.companyMissing) return { kind: 'no_company' };
  const company = fs.legalEntityName?.trim() || null;
  if (fs.approvers == null) return { kind: 'unknown', company };
  if (fs.approvers <= 0) return { kind: 'nobody', company };
  return { kind: 'waiting', company };
}

export const FinanceStageNotice: React.FC<{ stage: FinanceStage | null | undefined }> = ({ stage }) => {
  const { t } = useTranslation(['expenses']);
  const s = financeStageState(stage);
  if (!s) return null;

  if (s.kind === 'waiting' || s.kind === 'unknown') {
    const text = s.company
      ? t('expenses:finance_stage.waiting', { defaultValue: 'Waiting on Finance for {{company}}.', company: s.company })
      : t('expenses:finance_stage.waiting_no_name', { defaultValue: "Waiting on Finance for the employee's company." });
    return (
      <p role="status" className="flex items-start gap-2 text-sm rounded-md border border-border bg-muted/40 px-3 py-2 text-foreground">
        <Clock className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <span>{text}</span>
      </p>
    );
  }

  const text = s.kind === 'nobody'
    ? (s.company
      ? t('expenses:finance_stage.nobody', {
        defaultValue: 'No one can approve this at the finance stage for {{company}} — give someone Finance access for it (Roles & permissions → Assignments).',
        company: s.company,
      })
      : t('expenses:finance_stage.nobody_no_name', {
        defaultValue: "No one can approve this at the finance stage for the employee's company — give someone Finance access for it (Roles & permissions → Assignments).",
      }))
    : t('expenses:finance_stage.no_company', {
      defaultValue: "This expense has no company, so only Finance with access to all companies can approve it. Set the employee's company so their company's Finance is asked.",
    });
  return (
    <div role="alert" className="flex items-start gap-2 text-sm rounded-md border border-warning bg-warning-soft px-3 py-2 text-foreground">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning-strong" />
      <span>{text}</span>
    </div>
  );
};
